import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiCall } from 'services';
import { isLocalhost } from 'utils';

/**
 * VERA-304 (A1) — "Verify possession": raise the certificate's latest completed
 * transfer from `claimed` (or `transferred`) to `scan_claimed`, on proof that
 * the current owner scanned the physical tag.
 *
 * `token` is the mobile-scan token a physical tap produces — the SAME credential
 * `POST /mobile/scan` takes. There is no "I scanned it" flag: the server decodes
 * the token's payload and requires it to resolve to this certificate's own tag,
 * so nothing the client can assert substitutes for the scan.
 */
interface VerifyPossessionData {
  certificateId: string;
  token: string;
}

export interface VerifyPossessionResult {
  transferId: string;
  /** False when the transfer was already `scan_claimed` — a no-op, not a failure. */
  upgraded: boolean;
  completionMethod: string;
}

const verifyPossession = async ({ certificateId, token }: VerifyPossessionData) =>
  createApiCall({
    method: 'POST',
    url: `/transfer/customer/verify-possession/${certificateId}`,
    data: { token },
    throwError: true,
  });

export const useVerifyPossession = (certificateId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VerifyPossessionData) => verifyPossession(data),
    onSuccess: () => {
      // Both keys, for the same reason `useClaimTransferByToken` invalidates
      // both: the affordance that launched this reads the DETAIL query
      // (`certificate.transfer.completion_method`) and disappears only once that
      // refetches, while the list carries the same chip on its cards.
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      queryClient.invalidateQueries({
        queryKey: certificateId ? ['certificate-public', certificateId] : ['certificate-public'],
      });
    },
  });
};

/**
 * DEV ONLY — obtain a REAL mobile-scan token for a certificate's own tag, so the
 * possession flow can be walked on a desktop preview that has no NFC radio.
 *
 * ## Why a minted token rather than a client-side bypass
 *
 * The verify-possession endpoint has one code path and no dev branch, by design
 * — a bypass there would be a hole in the exact check the feature exists to make.
 * So the dev affordance sits one layer up: the API mints a genuine, signed token
 * behind `NODE_ENV !== 'production'` + `DEV_SESSION_SECRET`, and the production
 * endpoint then verifies it unchanged and unaware. What the preview demonstrates
 * is therefore the real flow, not a mock of it.
 *
 * Gated client-side on BOTH `VITE_DEV_SESSION` and a loopback hostname, the same
 * two conditions as the dev customer-session auto-login and `ScanTag`'s own
 * simulate button — and the API rejects independently, so a mis-set flag on a
 * deployed preview cannot reach it.
 */
export const mintDevScanToken = async (certificateId: string): Promise<string | null> => {
  if (!import.meta.env.VITE_DEV_SESSION || !isLocalhost()) return null;

  const resp = await createApiCall({
    method: 'POST',
    url: '/mobile/scan/dev-token',
    data: {
      secret: import.meta.env.VITE_DEV_SESSION_SECRET,
      certificateId,
    },
  });

  return resp?.data?.token ?? null;
};
